/**
 * AI agent / search crawler tracking, extracted from snippets/seo-meta-global.liquid.
 *
 * It was a 427-line inline <script> in <head>. `defer` and `async` are no-ops
 * on an inline script (the HTML spec forbids them without `src`), so that block
 * was parse-blocking on every page, and inline script is uncacheable so it was
 * re-downloaded inside every HTML response. Only an external src + real defer
 * actually defers.
 *
 * The 13 Liquid interpolations it carried are all already published by the
 * FoxTheme.page island in snippets/js-variables.liquid, so this needs no Liquid
 * of its own and no second inline config block. js-variables.liquid renders
 * inline in <head> and this file is deferred, so the island always exists first.
 */
(function () {
  'use strict';

  const PAGE = (window.FoxTheme && window.FoxTheme.page) || {};
  const PRODUCT = PAGE.product || {};

  const PAGE_PATH = PAGE.path || (window.location && window.location.pathname) || '';
  const PAGE_TYPE = PAGE.pageType || '';
  const PRODUCT_ID = PRODUCT.id == null ? '' : String(PRODUCT.id);
  const PRODUCT_NAME = PRODUCT.title || '';
  const PRODUCT_TYPE = PRODUCT.type || '';
  // priceAmount is money_without_currency, so "1,234.50" above four figures.
  const PRODUCT_PRICE = Number(String(PRODUCT.priceAmount || '0').replace(/,/g, '')) || 0;
  // The Liquid this replaces emitted this one quoted, so the string form is kept
  // rather than silently changing the field's type in the tracked payload.
  const PRODUCT_PRICE_STR = String(PRODUCT_PRICE);

  const AIAgentTracker = {
    // List of known AI agent user agents
    aiAgents: {
      GPTBot: { name: 'ChatGPT', type: 'crawler' },
      'ChatGPT-User': { name: 'ChatGPT', type: 'user' },
      ClaudeBot: { name: 'Claude', type: 'crawler' },
      'anthropic-ai': { name: 'Claude', type: 'crawler' },
      PerplexityBot: { name: 'Perplexity', type: 'crawler' },
      Perplexity: { name: 'Perplexity', type: 'user' },
      'Google-Extended': { name: 'Google Bard/Gemini', type: 'crawler' },
      'Googlebot-Extended': { name: 'Google Gemini', type: 'crawler' },
      Applebot: { name: 'Apple Intelligence', type: 'crawler' },
      CCBot: { name: 'Common Crawl (AI Training)', type: 'crawler' },
      Diffbot: { name: 'Diffbot', type: 'crawler' },
      FacebookBot: { name: 'Meta AI', type: 'crawler' },
      Bytespider: { name: 'TikTok AI', type: 'crawler' },
    },

    // Search engine crawlers
    searchCrawlers: {
      Googlebot: { name: 'Google', priority: 'high' },
      Bingbot: { name: 'Bing', priority: 'high' },
      Slurp: { name: 'Yahoo', priority: 'medium' },
      DuckDuckBot: { name: 'DuckDuckGo', priority: 'medium' },
      Baiduspider: { name: 'Baidu', priority: 'medium' },
      YandexBot: { name: 'Yandex', priority: 'medium' },
      Sogou: { name: 'Sogou', priority: 'low' },
      Exabot: { name: 'Exalead', priority: 'low' },
      facebot: { name: 'Facebook', priority: 'medium' },
      ia_archiver: { name: 'Alexa', priority: 'low' },
    },

    // Blocked/suspicious bots
    blockedBots: {
      AhrefsBot: { name: 'Ahrefs', reason: 'SEO tool' },
      SemrushBot: { name: 'Semrush', reason: 'SEO tool' },
      MJ12bot: { name: 'Majestic', reason: 'SEO tool' },
      DotBot: { name: 'DotBot', reason: 'scraper' },
      MegaIndex: { name: 'MegaIndex', reason: 'scraper' },
      BLEXBot: { name: 'BLEXBot', reason: 'scraper' },
      PetalBot: { name: 'PetalBot', reason: 'aggressive crawler' },
      Seekport: { name: 'Seekport', reason: 'scraper' },
      SurveyBot: { name: 'SurveyBot', reason: 'scraper' },
      DataForSeoBot: { name: 'DataForSEO', reason: 'SEO tool' },
    },

    // Detect AI agent from user agent string
    detectAIAgent: function () {
      const userAgent = navigator.userAgent;

      for (const [botString, botInfo] of Object.entries(this.aiAgents)) {
        if (userAgent.includes(botString)) {
          return botInfo.name;
        }
      }

      // Check UTM parameters for AI traffic
      const urlParams = new URLSearchParams(window.location.search);
      const utmSource = urlParams.get('utm_source') || '';

      if (
        ['chatgpt', 'claude', 'perplexity', 'gemini', 'bard', 'copilot', 'ai-agent'].includes(
          utmSource.toLowerCase()
        )
      ) {
        return utmSource.charAt(0).toUpperCase() + utmSource.slice(1);
      }

      // Check referrer for AI platforms
      const referrer = document.referrer.toLowerCase();
      if (referrer.includes('chat.openai.com')) return 'ChatGPT';
      if (referrer.includes('claude.ai')) return 'Claude';
      if (referrer.includes('perplexity.ai')) return 'Perplexity';
      if (referrer.includes('gemini.google.com') || referrer.includes('bard.google.com')) {
        return 'Google Gemini';
      }
      if (referrer.includes('copilot.microsoft.com')) return 'Microsoft Copilot';

      return null;
    },

    // Detect search crawler from user agent
    detectSearchCrawler: function () {
      const userAgent = navigator.userAgent;

      for (const [botString, botInfo] of Object.entries(this.searchCrawlers)) {
        if (userAgent.includes(botString)) {
          return botInfo.name;
        }
      }

      return null;
    },

    // Detect blocked/suspicious bot
    detectBlockedBot: function () {
      const userAgent = navigator.userAgent;

      for (const [botString, botInfo] of Object.entries(this.blockedBots)) {
        if (userAgent.includes(botString)) {
          return { name: botInfo.name, reason: botInfo.reason };
        }
      }

      return null;
    },

    // Track search crawler visit
    trackSearchCrawler: function (crawler) {
      const trackingData = {
        crawler: crawler,
        page_path: PAGE_PATH,
        page_type: PAGE_TYPE,
        timestamp: Date.now(),
      };

      // Store in localStorage
      const visits = JSON.parse(localStorage.getItem('search_crawler_visits') || '[]');
      visits.push(trackingData);

      // Keep only last 100 visits
      if (visits.length > 100) {
        visits.shift();
      }

      localStorage.setItem('search_crawler_visits', JSON.stringify(visits));
    },

    // Track blocked bot attempt
    trackBlockedBot: function (botData) {
      const trackingData = {
        bot: botData.name,
        reason: botData.reason,
        page_path: PAGE_PATH,
        timestamp: Date.now(),
      };

      // Store in localStorage
      const attempts = JSON.parse(localStorage.getItem('blocked_bot_attempts') || '[]');
      attempts.push(trackingData);

      // Keep only last 100 attempts
      if (attempts.length > 100) {
        attempts.shift();
      }

      localStorage.setItem('blocked_bot_attempts', JSON.stringify(attempts));
    },

    // Track AI agent visit
    trackAIVisit: function (aiAgent) {
      const pageType = PAGE_TYPE;
      const pagePath = PAGE_PATH;
      const productId = PRODUCT_ID;
      const productName = PRODUCT_NAME;
      const productPrice = PRODUCT_PRICE_STR;

      const trackingData = {
        ai_agent: aiAgent,
        page_type: pageType,
        page_path: pagePath,
        product_id: productId,
        product_name: productName,
        product_price: productPrice,
        timestamp: Date.now(),
        session_id: this.getSessionId(),
      };

      // Send to Google Analytics 4
      if (window.gtag) {
        gtag('event', 'ai_agent_visit', {
          event_category: 'AI Traffic',
          event_label: aiAgent,
          ai_agent: aiAgent,
          page_type: pageType,
          product_id: productId,
          product_name: productName,
          product_price: productPrice,
          traffic_source: 'ai_commerce',
        });
      }

      // Send to dataLayer for GTM
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'ai_agent_visit',
          ai_agent: aiAgent,
          page_type: pageType,
          page_path: pagePath,
          product_id: productId,
          visit_timestamp: Date.now(),
        });
      }

      // Store in localStorage for session tracking
      this.storeAIVisit(trackingData);

      // Send to custom endpoint for Shopify integration
      this.sendToShopify(trackingData);
    },

    // Track AI-driven purchases
    trackAIPurchase: function (orderData) {
      const aiAgent = sessionStorage.getItem('ai_agent_source');

      if (!aiAgent) return; // Not an AI-driven session

      const purchaseData = {
        ai_agent: aiAgent,
        order_id: orderData.order_id,
        order_value: orderData.total_price,
        products: orderData.line_items,
        timestamp: Date.now(),
      };

      // Send to GA4
      if (window.gtag) {
        gtag('event', 'purchase_from_ai', {
          event_category: 'AI Conversions',
          event_label: aiAgent,
          ai_agent: aiAgent,
          transaction_id: orderData.order_id,
          value: orderData.total_price,
          currency: 'USD',
          items: orderData.line_items,
        });
      }

      // Send to dataLayer
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'ai_purchase',
          ai_agent: aiAgent,
          order_id: orderData.order_id,
          order_value: orderData.total_price,
          conversion_type: 'ai_commerce',
        });
      }

      // Store AI conversion
      this.storeAIConversion(purchaseData);
    },

    // Get or create session ID
    getSessionId: function () {
      let sessionId = sessionStorage.getItem('ai_session_id');
      if (!sessionId) {
        sessionId = 'ai_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        sessionStorage.setItem('ai_session_id', sessionId);
      }
      return sessionId;
    },

    // Store AI visit in localStorage
    storeAIVisit: function (data) {
      const visits = JSON.parse(localStorage.getItem('ai_agent_visits') || '[]');
      visits.push(data);

      // Keep only last 100 visits
      if (visits.length > 100) {
        visits.shift();
      }

      localStorage.setItem('ai_agent_visits', JSON.stringify(visits));
    },

    // Store AI conversion
    storeAIConversion: function (data) {
      const conversions = JSON.parse(localStorage.getItem('ai_conversions') || '[]');
      conversions.push(data);
      localStorage.setItem('ai_conversions', JSON.stringify(conversions));
    },

    // Send to Shopify for custom reports
    sendToShopify: function (data) {
      // Use Shopify's customer metafields or custom app for storage
      // This would require a custom Shopify app or webhook

      // For now, we'll use Shopify's built-in analytics via custom properties
      if (window.ShopifyAnalytics) {
        window.ShopifyAnalytics.lib.track('AI Agent Visit', {
          ai_agent: data.ai_agent,
          page_type: data.page_type,
          product_id: data.product_id,
        });
      }
    },

    // Initialize tracking
    init: function () {
      // Check for blocked bots first (highest priority)
      const blockedBot = this.detectBlockedBot();
      if (blockedBot) {
        this.trackBlockedBot(blockedBot);
        return; // Don't track further if it's a blocked bot
      }

      // Check for search crawlers
      const searchCrawler = this.detectSearchCrawler();
      if (searchCrawler) {
        this.trackSearchCrawler(searchCrawler);
        return; // Don't track as AI agent if it's a search crawler
      }

      // Check for AI agents
      const aiAgent = this.detectAIAgent();

      if (aiAgent) {
        // Store AI agent for session
        sessionStorage.setItem('ai_agent_source', aiAgent);

        // Track the visit
        this.trackAIVisit(aiAgent);

        // Track product views
        if (PAGE_TYPE === 'product') {
          if (window.gtag) {
            gtag('event', 'view_item_from_ai', {
              event_category: 'AI Product Views',
              event_label: aiAgent,
              ai_agent: aiAgent,
              items: [
                {
                  id: PRODUCT_ID,
                  name: PRODUCT_NAME,
                  price: PRODUCT_PRICE,
                  category: PRODUCT_TYPE,
                },
              ],
            });
          }
        }

        // Track add to cart from AI traffic
        document.addEventListener('submit', function (e) {
          const form = e.target;
          if (form.action && form.action.includes('/cart/add')) {
            if (window.gtag) {
              gtag('event', 'add_to_cart_from_ai', {
                event_category: 'AI Commerce Actions',
                event_label: aiAgent,
                ai_agent: aiAgent,
                product_id: form.querySelector('[name="id"]')?.value,
              });
            }
          }
        });
      }

      // Check if this is thank you page (purchase complete)
      if (window.Shopify && window.Shopify.checkout && window.Shopify.checkout.order_id) {
        this.trackAIPurchase({
          order_id: window.Shopify.checkout.order_id,
          total_price: window.Shopify.checkout.total_price,
          line_items: window.Shopify.checkout.line_items,
        });
      }
    },

    // Get AI traffic stats (for debugging/dashboard)
    getStats: function () {
      const visits = JSON.parse(localStorage.getItem('ai_agent_visits') || '[]');
      const conversions = JSON.parse(localStorage.getItem('ai_conversions') || '[]');
      const crawlerVisits = JSON.parse(localStorage.getItem('search_crawler_visits') || '[]');
      const blockedAttempts = JSON.parse(localStorage.getItem('blocked_bot_attempts') || '[]');

      const stats = {
        total_visits: visits.length,
        total_conversions: conversions.length,
        conversion_rate:
          visits.length > 0 ? ((conversions.length / visits.length) * 100).toFixed(2) + '%' : '0%',
        by_agent: {},
        search_crawlers: {
          total_visits: crawlerVisits.length,
          by_crawler: {},
        },
        blocked_bots: {
          total_attempts: blockedAttempts.length,
          by_bot: {},
        },
      };

      // Count by agent
      visits.forEach((visit) => {
        if (!stats.by_agent[visit.ai_agent]) {
          stats.by_agent[visit.ai_agent] = { visits: 0, conversions: 0, revenue: 0 };
        }
        stats.by_agent[visit.ai_agent].visits++;
      });

      conversions.forEach((conv) => {
        if (stats.by_agent[conv.ai_agent]) {
          stats.by_agent[conv.ai_agent].conversions++;
          stats.by_agent[conv.ai_agent].revenue += parseFloat(conv.order_value) || 0;
        }
      });

      // Count crawlers
      crawlerVisits.forEach((visit) => {
        if (!stats.search_crawlers.by_crawler[visit.crawler]) {
          stats.search_crawlers.by_crawler[visit.crawler] = 0;
        }
        stats.search_crawlers.by_crawler[visit.crawler]++;
      });

      // Count blocked bots
      blockedAttempts.forEach((attempt) => {
        if (!stats.blocked_bots.by_bot[attempt.bot]) {
          stats.blocked_bots.by_bot[attempt.bot] = { count: 0, reason: attempt.reason };
        }
        stats.blocked_bots.by_bot[attempt.bot].count++;
      });

      return stats;
    },
  };

  // Make available globally immediately
  window.AIAgentTracker = AIAgentTracker;

  // Auto-initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      AIAgentTracker.init();
    });
  } else {
    AIAgentTracker.init();
  }

  // Debug command: AIAgentTracker.getStats() in console
})();
